import { Button, IconButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import { reduce } from 'lodash';
import { LIST_ROW_HEIGHTS } from '../../../../constants/list_row_heights';
import {
  closeInnerPopup,
  closePopup,
  openInnerPopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { ReportSettings } from '../../../../data/providers/reports/types';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { ManageUsers } from '../../../settings/manage_users/ManageUsers';
import { Card } from '../../../shared/card/Card';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import { CardHeader, CardHeaderLeft } from '../../../shared/card/CardHeader';
import { SearchInput } from '../../../shared/form/SearchInput';
import { Step } from '../../../shared/form/Step';
import { Icon } from '../../../shared/Icon';
import { IListRowRenderProps, List } from '../../../shared/List/List';
import { ListControlContainer } from '../../../shared/List/ListControlContainer';
import {
  ListItem,
  ListItemCell,
  ListItemSafeText,
} from '../../../shared/List/ListItem';
import { NoResults } from '../../../shared/NoResults';
import { FormSection } from './FormSection';
import { ITemplateFormProps } from './types';

interface IProps extends RouteComponentProps {
  formComponent: React.FC<ITemplateFormProps>;
  onDelete: (template: ISettingsTemplate<ReportSettings>) => void;
  onSelect: (template: ISettingsTemplate<ReportSettings>) => void;
  reportType?: string;
  reportTypeId?: string;
  searchTestId?: string;
  selectedTemplateId: string;
  step?: number;
  templates: Array<ISettingsTemplate<ReportSettings>>;
  testId?: string;
}

export const TemplateSelect = withRouter(
  (props: IProps): React.ReactElement => {
    const [search, setSearch] = React.useState<string>('');

    /**
     * @description handler template deletion
     */
    const deleteHandler = (
      template: ISettingsTemplate<ReportSettings>
    ): void => {
      const acknowledgment = (): void => {
        props.onDelete(template);
      };
      openInnerPopup(
        <DeleteAcknowledgment
          templateName={template.name}
          onCancel={closeInnerPopup(props.history)}
          onAcknowledge={acknowledgment}
        />,
        props.history,
        'confirm-template-delete'
      );
    };

    /**
     * @description handles opening edit/create form
     * @param template
     * @param isSelected
     */
    const openFormHandler = (
      template?: ISettingsTemplate<ReportSettings>,
      isSelected?: boolean
    ): void => {
      const tag = template
        ? 'edit-settings-template'
        : 'create-settings-template';

      const Form = props.formComponent;

      openInnerPopup(
        <Form
          onClose={closeInnerPopup(props.history)}
          template={template}
          isSelected={isSelected}
        />,
        props.history,
        tag
      );
    };

    /**
     * @description opens the dialog to copy a template
     * @param template
     */
    const copyHandler = (template: ISettingsTemplate<ReportSettings>): void => {
      const copiedTemplate: ISettingsTemplate<ReportSettings> = {
        ...template,
        name: `${template.name}-Copy`,
        id: null,
        shared: false,
      };

      const Form = props.formComponent;

      openInnerPopup(
        <Form
          onClose={closeInnerPopup(props.history)}
          template={copiedTemplate}
          isSelected={false}
        />,
        props.history,
        'create-settings-template'
      );
    };

    /**
     * @description handles the manage users action
     */
    const openManageUsers = (
      template: ISettingsTemplate<ReportSettings>
    ): void => {
      openPopup(
        <ManageUsers
          onClose={closePopup(props.history)}
          onSelect={null}
          reportType={props.reportType}
          reportTypeId={props.reportTypeId}
          selected={false}
          template={template}
          shareTemplate
        />,
        props.history,
        'manage-users'
      );
    };

    const onCreate = (_: React.MouseEvent<HTMLButtonElement>): void =>
      openFormHandler();

    /**
     * @description render function for list items
     * @param param0
     */
    const rowRenderer = ({
      index,
      key,
      style,
    }: IListRowRenderProps): React.ReactElement => {
      const row = data[index];
      const selected = row.id === props.selectedTemplateId;
      const editHandler = (): void => {
        openFormHandler(row, selected);
      };

      return (
        <TemplateListItem
          search={search}
          selected={selected}
          style={style}
          template={row}
          key={key}
          onCopy={copyHandler}
          onSelect={props.onSelect}
          onEdit={editHandler}
          onDelete={deleteHandler}
          onShare={openManageUsers}
        />
      );
    };

    /**
     * @description clear search
     */
    const clearHandler = (): void => setSearch('');

    /**
     * @description filter templates based on search
     */
    const data = React.useMemo((): Array<ISettingsTemplate<ReportSettings>> => {
      if (isEmpty(search)) return props.templates;
      return props.templates.filter(
        (template: ISettingsTemplate<ReportSettings>): boolean => {
          return (
            template.name?.toLowerCase().includes(search.toLowerCase()) ||
            template.description?.toLowerCase().includes(search.toLowerCase())
          );
        }
      );
    }, [search, JSON.stringify(props.templates)]);

    return (
      <Container data-testid={props.testId}>
        <CardHeader>
          <CardHeaderLeft>
            {props.step && <Step>{props.step}</Step>}
            <div>
              <h6>Select Template</h6>
            </div>
          </CardHeaderLeft>
        </CardHeader>
        <HeaderRow>
          <HeaderLeft>
            <SearchInput
              style={{ marginLeft: '0px' }}
              value={search}
              onChange={setSearch}
              onClear={clearHandler}
              testId={props.searchTestId}
            />
          </HeaderLeft>
          <HeaderRight>
            <Button
              icon='assignment_add'
              onClick={onCreate}
              title='Create Template'
              data-testid={`${props.reportType
                .replace(/ /g, '-')
                .toLowerCase()}-create-template-button`}
            />
          </HeaderRight>
        </HeaderRow>
        <ListControlContainer style={{ flex: 1 }}>
          {data?.length > 0 ? (
            <List
              rowHeight={LIST_ROW_HEIGHTS.normal}
              rowRenderer={rowRenderer}
              listLength={data?.length}
            />
          ) : (
            <React.Fragment>
              <NoResults
                message='No Settings Templates'
                filterApplied={search.length > 0}
                clearFilter={clearHandler}
              />
            </React.Fragment>
          )}
        </ListControlContainer>
      </Container>
    );
  }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Container = styled(FormSection as any)`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

interface ITemplateItemProps {
  search?: string;
  template: ISettingsTemplate<ReportSettings>;
  style: React.CSSProperties;
  onSelect: (template: ISettingsTemplate<ReportSettings>) => void;
  onEdit: (template: ISettingsTemplate<ReportSettings>) => void;
  onCopy: (template: ISettingsTemplate<ReportSettings>) => void;
  onDelete: (template: ISettingsTemplate<ReportSettings>) => void;
  onShare: (template: ISettingsTemplate<ReportSettings>) => void;
  selected?: boolean;
}

const TemplateListItem = (props: ITemplateItemProps): React.ReactElement => {
  const {
    template,
    style,
    selected,
    onSelect,
    onDelete,
    onEdit,
    onCopy,
    onShare,
  } = props;
  const selectHandler = (): void => onSelect(template);
  const editHandler = (): void => onEdit(template);
  const deleteHandler = (): void => onDelete(template);
  const copyHandler = (): void => onCopy(template);
  const shareHandler = (): void => onShare(template);

  return (
    <ListItem
      style={{ height: '100px', ...style }}
      onClick={selectHandler}
      selected={selected}
    >
      <ListItemCell style={{ flex: 2 }}>
        <Row style={{ alignItems: 'center' }}>
          <SelectCircle>
            {selected && (
              <Icon
                name='check'
                style={{ color: 'var(--color-material-500-green)' }}
              />
            )}
          </SelectCircle>
          <Column>
            <div>
              <span>{template.name}</span>
            </div>
            <Description>
              <ListItemSafeText>
                {template?.description ? template.description : null}
              </ListItemSafeText>
            </Description>
          </Column>
        </Row>
      </ListItemCell>
      <ListItemCell
        style={{ flex: '0 0 auto', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}
      >
        <IconButton
          onClick={editHandler}
          icon='edit'
          title='Edit Template'
          disabled={template.shared || template.hawkinCreated}
          style={{ margin: '0 .2rem' }}
        />
        <IconButton
          onClick={shareHandler}
          icon='share'
          disabled={template?.hawkinCreated}
          title='Share Template'
          style={{ margin: '0 .2rem' }}
        />
        <IconButton
          onClick={copyHandler}
          disabled={template?.hawkinCreated}
          icon='content_copy'
          title='Copy Template'
          style={{ margin: '0 .2rem' }}
        />
        <IconButton
          onClick={deleteHandler}
          icon='delete'
          title='Delete Template'
          disabled={template.shared || template?.hawkinCreated}
          style={{ margin: '0 .2rem' }}
        />
      </ListItemCell>
    </ListItem>
  );
};

interface IDeleteAckProps {
  onCancel: () => void;
  onAcknowledge: () => void;
  templateName: string;
}
const DeleteAcknowledgment = (props: IDeleteAckProps): React.ReactElement => {
  const deleteHandler = (): void => {
    props.onAcknowledge();
    props.onCancel();
  };
  return (
    <Card size='small'>
      <Column>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Confirm Delete</h5>
          </CardHeaderLeft>
        </CardHeader>
        <CardBody>
          <div style={{ padding: '10px' }}>
            <h5>
              Are you sure you want to delete the {props.templateName} template?
              This action will remove the template from your database.
            </h5>
          </div>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <Button onClick={props.onCancel} title='No' />
            <Button onClick={deleteHandler} title='Yes' />
          </CardFooterRight>
        </CardFooter>
      </Column>
    </Card>
  );
};

const Description = styled.div`
  color: var(--color-secondary-text);
`;

const SelectCircle = styled.div`
  padding: 0;
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
`;

const HeaderRow = styled.header`
  display: flex;
  flex-direction: row;
  padding: 10px;
`;

const HeaderRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const HeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
`;
